/**
 * S3Upload — Reusable file upload component.
 * Gets a pre-signed URL from Cloud Functions, uploads directly to S3,
 * returns the public URL.
 */
import React, { useState, useRef } from 'react';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';

const getUploadUrl = httpsCallable(functions, 'getChampionshipUploadUrl');

/**
 * @param {string}   folder    - S3 folder path (e.g. 'championships/drivers/abc123')
 * @param {string}   accept    - File input accept (e.g. 'image/*', 'video/*')
 * @param {function} onUploaded - Called with { publicUrl, key } after successful upload
 * @param {string}   label     - Button label
 * @param {string}   currentUrl - Current file URL for preview
 */
export default function S3Upload({ folder, accept = 'image/*', onUploaded, label = 'Upload', currentUrl }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentUrl || '');
  const inputRef = useRef(null);

  const isVideo = accept.includes('video');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Size limits: 10MB for images, 100MB for videos
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Max ${isVideo ? '100MB' : '10MB'}.`);
      return;
    }

    setUploading(true);
    setError('');
    setProgress(0);

    try {
      // 1. Get presigned URL from Cloud Function
      const result = await getUploadUrl({
        fileName: file.name,
        contentType: file.type,
        folder: folder,
      });

      const { uploadUrl, publicUrl } = result.data;

      // 2. Upload directly to S3 via PUT
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (evt) => {
          if (evt.lengthComputable) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // 3. Set preview and notify parent
      if (!isVideo) setPreview(publicUrl);
      onUploaded?.({ publicUrl, key: result.data.key });
      setProgress(100);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      {/* Preview */}
      {preview && !isVideo && (
        <div style={{
          width: 80, height: 80, borderRadius: 8, overflow: 'hidden',
          marginBottom: 8, border: '1px solid var(--border)',
          background: 'var(--bg-input)',
        }}>
          <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}

      {/* Upload button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          style={{ display: 'none' }}
          id={`upload-${folder}`}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          type="button"
        >
          {uploading ? `Uploading ${progress}%` : label}
        </button>

        {uploading && (
          <div style={{
            flex: 1, height: 4, borderRadius: 2,
            background: 'rgba(255,255,255,0.08)', overflow: 'hidden', maxWidth: 150,
          }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              background: 'var(--teal)', borderRadius: 2,
              transition: 'width 0.2s ease',
            }} />
          </div>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
